/**
 * Cuestionario: estructura, recortes del piloto y experimento de etiqueta.
 *
 * La vista es de lectura para la estructura y de edición sólo para lo que el
 * documento contempla ajustar: los recortes de §8.1 tras el piloto y las celdas
 * del experimento de §Bloque 8.
 *
 * Los reactivos que §9.6 congela —N1, I1–I5, P1 y P4— se marcan como
 * tales y NO son editables desde aquí: cambiar su redacción, escala u orden
 * rompería la comparabilidad de la serie de tiempo entre oleadas, que es el
 * propósito de tener una línea base.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../api/client';
import { useAdmin } from '../store';
import { BarChart, StatusChip, Tile, fmtDecimal, fmtInt } from '../components/charts';

export function InstrumentPage() {
  const { can } = useAdmin();
  const queryClient = useQueryClient();
  const [openBlock, setOpenBlock] = useState<number | null>(0);

  const panel = useQuery({ queryKey: ['instrument', 'panel'], queryFn: api.instrumentPanel });
  const balance = useQuery({
    queryKey: ['monitoring', 'experiment-balance'],
    queryFn: api.experimentBalance,
  });
  const i6 = useQuery({ queryKey: ['monitoring', 'i6-coverage'], queryFn: api.i6Coverage });

  const toggleTrim = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.toggleTrim(key, enabled),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['instrument'] }),
  });

  if (panel.isLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }

  const data = panel.data;
  if (!data) {
    return <div className="callout callout--critical">No se pudo cargar el instrumento.</div>;
  }

  const experiment = data.experiments[0];

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Cuestionario</h1>
        <p className="page-sub">
          Instrumento {data.version} · metodología {data.methodologyVersion} ·{' '}
          {data.wave.label}
        </p>
      </div>

      <WaveStage wave={data.wave} />

      <div className="grid grid--4">
        <Tile label="Bloques" value={fmtInt(data.totals.blocks)} reference="§7" />
        <Tile label="Preguntas" value={fmtInt(data.totals.questions)} />
        <Tile
          label="Duración de diseño"
          reference="§2"
          value={`${data.estimatedMinutes.min}–${data.estimatedMinutes.max}`}
          unit="min"
          meta={`Tabla de §7: ${data.totals.estimatedMinutesLow}–${data.totals.estimatedMinutesHigh} min`}
        />
        <Tile
          label="Reactivos congelados"
          reference="§9.6"
          value={fmtInt(data.frozenReactives.length)}
          meta="No cambian entre oleadas"
        />
      </div>

      {/* La duración es un dato que se vigila en los dos sentidos: si se pasa
          de la meta hay que recortar, y si queda muy por debajo conviene
          revisar que no se haya caído algo del cuestionario sin querer. */}
      {data.totals.estimatedMinutesHigh > data.estimatedMinutes.max ? (
        <div
          className={
            data.totals.estimatedMinutesLow > data.estimatedMinutes.max
              ? 'callout callout--warning'
              : 'callout callout--info'
          }
        >
          <span className="callout__title">Sobre la duración</span>
          La tabla de bloques de §7 suma {data.totals.estimatedMinutesLow}–
          {data.totals.estimatedMinutesHigh} minutos, por encima de los{' '}
          {data.estimatedMinutes.min}–{data.estimatedMinutes.max} que declara §2. §8 existe para
          medir la duración real en el piloto de 30–40 entrevistas y, si se pasa de 20 minutos,
          aplicar los recortes de §8.1 en orden.
        </div>
      ) : (
        <div className="callout callout--good">
          <span className="callout__title">Sobre la duración</span>
          La tabla de bloques de §7 suma {data.totals.estimatedMinutesLow}–
          {data.totals.estimatedMinutesHigh} minutos y cabe en los{' '}
          {data.estimatedMinutes.min}–{data.estimatedMinutes.max} de §2. Los recortes de §8.1 no
          hacen falta; quedan de reserva por si el piloto mide una duración real mayor que la
          estimada, que es justo lo que §8 manda comprobar.
        </div>
      )}

      {/* ----------------- Cambios respecto a la metodología ----------------- */}

      {/* §9.6: la batería alimenta reactivos congelados, así que todo cambio
          tiene que estar a la vista de quien coordina el campo y de quien
          después compare W1 con W2. */}
      {data.changeLog.length > 0 ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                Cambios respecto a la metodología <span className="ref">§9.6</span>
              </h2>
              <p className="panel__note">
                Los reactivos congelados sólo pueden cambiar con la oleada en piloto y sin
                entrevistas levantadas. Cada cambio queda asentado con su fecha y su
                justificación.
              </p>
            </div>
          </div>

          <div className="stack">
            {data.changeLog.map((change) => (
              <div
                key={change.version}
                style={{
                  padding: '12px 14px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                }}
              >
                <div className="row" style={{ marginBottom: 4 }}>
                  <span className="chip chip--accent">{change.version}</span>
                  <span className="faint">{change.date}</span>
                </div>
                <div>{change.note}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* ------------------------- Recortes del piloto ----------------------- */}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">
              Recortes del piloto <span className="ref">§8.1</span>
            </h2>
            <p className="panel__note">
              Se aplican EN ORDEN si el piloto supera 20 minutos. Todos arrancan desactivados: el
              cuestionario completo es el diseño de referencia.
            </p>
          </div>
        </div>

        <div className="stack">
          {data.trims.map((trim) => (
            <div
              key={trim.key}
              className="row row--between"
              style={{
                padding: '12px 14px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                gap: 16,
              }}
            >
              <div style={{ flex: 1 }}>
                <div className="row" style={{ marginBottom: 4 }}>
                  <span className="chip chip--accent">{trim.order}</span>
                  {trim.enabled ? (
                    <StatusChip status="warning">Activo</StatusChip>
                  ) : (
                    <StatusChip status="neutral">Inactivo</StatusChip>
                  )}
                </div>
                <div>{trim.description}</div>
                <div className="faint">
                  {trim.mode === 'subsample-rows'
                    ? `Conserva ${trim.rowsKept} filas por entrevistado`
                    : trim.mode === 'subsample-respondents'
                      ? `Se aplica al ${Math.round((trim.respondentShare ?? 0.5) * 100)}% de la muestra`
                      : `Reduce la asignación a ${trim.assignmentSize}`}
                </div>
              </div>
              {can('instrument:write') ? (
                <button
                  type="button"
                  className={`btn btn--sm ${trim.enabled ? 'btn--secondary' : 'btn--primary'}`}
                  onClick={() => toggleTrim.mutate({ key: trim.key, enabled: !trim.enabled })}
                  disabled={toggleTrim.isPending}
                >
                  {trim.enabled ? 'Desactivar' : 'Activar'}
                </button>
              ) : null}
            </div>
          ))}
        </div>

        {toggleTrim.data && toggleTrim.data.warnings.length > 0 ? (
          <div className="callout callout--warning" style={{ marginTop: 14, marginBottom: 0 }}>
            {toggleTrim.data.warnings.map((warning, i) => (
              <p key={i} style={{ margin: '4px 0' }}>
                {warning}
              </p>
            ))}
          </div>
        ) : null}
      </div>

      {/* --------------------------- Experimento X1 -------------------------- */}

      {experiment ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                {experiment.title} <span className="ref">§Bloque 8</span>
              </h2>
              <p className="panel__note">{experiment.methodologyNote}</p>
            </div>
          </div>

          <div className="table-wrap">
            <table className="table table--numeric">
              <thead>
                <tr>
                  <th>Celda</th>
                  <th>Personaje</th>
                  <th>Etiqueta que se lee</th>
                  <th>Peso</th>
                  <th>Asignadas</th>
                  <th>No elegibles</th>
                  <th>Base efectiva</th>
                </tr>
              </thead>
              <tbody>
                {experiment.cells.map((cell) => {
                  const cellBalance = balance.data?.cells.find((c) => c.cellId === cell.id);
                  const personaje = data.personajes.find((p) => p.id === cell.personajeId);
                  return (
                    <tr key={cell.id}>
                      <td className="strong mono">{cell.id}</td>
                      <td style={{ textAlign: 'left' }}>
                        {personaje?.cardName ?? cell.personajeId}
                        <div className="faint">ref. interna: {personaje?.referenceParty}</div>
                      </td>
                      <td style={{ textAlign: 'left' }}>«{cell.labelText}»</td>
                      <td>{cell.weight}</td>
                      <td>{cellBalance ? fmtInt(cellBalance.assigned) : '—'}</td>
                      <td className="faint">
                        {cellBalance ? fmtInt(cellBalance.ineligible) : '—'}
                      </td>
                      <td className="strong">
                        {cellBalance ? fmtInt(cellBalance.effectiveBase) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {balance.data && balance.data.cells.some((c) => c.assigned > 0) ? (
            <>
              <p className="field__label" style={{ marginTop: 16 }}>
                Balanceo de celdas
              </p>
              <BarChart
                rows={balance.data.cells.map((cell) => ({
                  key: cell.cellId,
                  label: cell.cellId,
                  value: cell.effectiveBase,
                  valueLabel: `${fmtInt(cell.effectiveBase)} útiles`,
                  tooltip: `${cell.cellId} · ${cell.labelText}: ${fmtInt(cell.assigned)} asignadas, ${fmtInt(cell.ineligible)} no conocían al personaje, base efectiva ${fmtInt(cell.effectiveBase)}`,
                }))}
              />
            </>
          ) : null}

          <details className="note">
            <summary>Por qué «no elegible» no es «no respondió»</summary>
            <p>
              La celda se asigna antes de saber si el entrevistado conoce al personaje. Si no lo
              conoce, X1 no se aplica, pero la asignación queda registrada: así el denominador del
              efecto de etiqueta es el correcto y el analista distingue las dos situaciones. Mezclarlas
              sesgaría la comparación entre celdas, que es todo el propósito del experimento.
            </p>
          </details>
        </div>
      ) : null}

      {/* ------------------------- Cobertura de I6 --------------------------- */}

      {i6.data && i6.data.some((row) => row.responses > 0) ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                Cobertura de I6 por personaje <span className="ref">§Bloque 3</span>
              </h2>
              <p className="panel__note">
                I6 asigna 2 personajes conocidos por entrevistado priorizando los de MENOR
                cobertura. Si el mecanismo funciona, las barras deben irse igualando.
              </p>
            </div>
          </div>

          <BarChart
            rows={i6.data.map((row) => {
              const personaje = data.personajes.find((p) => p.id === row.personajeId);
              return {
                key: row.personajeId,
                label: personaje?.cardName ?? row.personajeId,
                value: row.responses,
                tooltip: `${personaje?.cardName ?? row.personajeId}: ${fmtInt(row.responses)} respuestas abiertas de I6`,
              };
            })}
          />
        </div>
      ) : null}

      {/* --------------------------- Estructura ------------------------------ */}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">
              Estructura del cuestionario <span className="ref">§7</span>
            </h2>
            <p className="panel__note">
              Primero lo espontáneo, luego lo asistido; primero personajes, luego partidos; las
              preguntas que mencionan partidos junto a personajes al final. Así ninguna pregunta
              «enseña» un nombre o una etiqueta partidista antes de medirse sin ayuda.
            </p>
          </div>
        </div>

        <div className="stack">
          {data.blocks.map((block) => (
            <div
              key={block.id}
              style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}
            >
              <button
                type="button"
                onClick={() => setOpenBlock(openBlock === block.id ? null : block.id)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  padding: '12px 14px',
                  cursor: 'pointer',
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                <div className="row row--between">
                  <span>
                    <span className="chip chip--accent">Bloque {block.id}</span>{' '}
                    <span className="strong">{block.title}</span>
                    {block.isCore ? (
                      <StatusChip status="accent">Núcleo</StatusChip>
                    ) : null}
                  </span>
                  <span className="faint num">
                    {block.questionCount} preguntas ·{' '}
                    {block.estimatedMinutesMax
                      ? `${block.estimatedMinutes}–${block.estimatedMinutesMax}`
                      : block.estimatedMinutes}{' '}
                    min
                  </span>
                </div>
              </button>

              {openBlock === block.id ? (
                <div style={{ padding: '0 14px 14px' }}>
                  {block.readAloudIntro ? (
                    <div
                      style={{
                        background: 'var(--accent-wash)',
                        borderLeft: '3px solid var(--accent)',
                        padding: '10px 12px',
                        borderRadius: '0 var(--radius) var(--radius) 0',
                        marginBottom: 12,
                        fontSize: 14,
                      }}
                    >
                      «{block.readAloudIntro}»
                    </div>
                  ) : null}

                  {block.orderingRationale ? (
                    <p className="faint" style={{ marginBottom: 12 }}>
                      {block.orderingRationale}
                    </p>
                  ) : null}

                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Pregunta</th>
                          <th>Tipo</th>
                          <th>Marcas</th>
                          <th>Obj.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {block.questions.map((question) => (
                          <tr key={question.code}>
                            <td className="strong mono">{question.code}</td>
                            <td style={{ maxWidth: 420 }}>
                              {question.text}
                              {question.methodologyNote ? (
                                <details className="note">
                                  <summary>Nota metodológica</summary>
                                  <p>{question.methodologyNote}</p>
                                </details>
                              ) : null}
                            </td>
                            <td className="faint">
                              {question.kind}
                              {question.rowSource ? (
                                <div className="faint">filas: {question.rowSource}</div>
                              ) : null}
                              {question.optionCount > 0 ? (
                                <div className="faint">{question.optionCount} opciones</div>
                              ) : null}
                            </td>
                            <td>
                              <div className="row row--wrap" style={{ gap: 4 }}>
                                {question.frozen ? (
                                  <StatusChip status="accent">Congelado §9.6</StatusChip>
                                ) : null}
                                {question.terminates ? (
                                  <StatusChip status="critical">Corta</StatusChip>
                                ) : null}
                                {question.hasSkipLogic ? (
                                  <span className="chip">Condicional</span>
                                ) : null}
                                {question.rotateRows || question.rotateOptions ? (
                                  <span className="chip">Rotación</span>
                                ) : null}
                                {question.trimKey ? (
                                  <span className="chip">Recortable</span>
                                ) : null}
                                {question.codeInOffice ? (
                                  <span className="chip">Codifica gabinete</span>
                                ) : null}
                                {question.isPii ? (
                                  <StatusChip status="warning">Dato personal</StatusChip>
                                ) : null}
                                {question.isVerificationKey ? (
                                  <span className="chip">Clave de verificación</span>
                                ) : null}
                              </div>
                            </td>
                            <td className="faint num">
                              {question.objectives.length > 0
                                ? question.objectives.join(', ')
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* --------------------------- Personajes ------------------------------ */}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">
              Los {data.personajes.length} personajes <span className="ref">§Bloque 3</span>
            </h2>
            <p className="panel__note">
              La tarjeta muestra ÚNICAMENTE el nombre de uso público: sin partido, sin foto y sin
              cargo. El partido de referencia es información interna para el análisis de asociación
              partidista (I4) y nunca se envía al dispositivo del encuestador.
            </p>
          </div>
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Nombre en tarjeta</th>
                <th>Partido de referencia (interno)</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {data.personajes.map((personaje) => (
                <tr key={personaje.id}>
                  <td className="strong">{personaje.cardName}</td>
                  <td className="faint">{personaje.referenceParty}</td>
                  <td className="faint" style={{ maxWidth: 520 }}>
                    {personaje.notes ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                          Etapa de la oleada                                */
/* -------------------------------------------------------------------------- */

const ETAPAS: { value: string; label: string; nota: string }[] = [
  { value: 'draft', label: 'Borrador', nota: 'No se puede capturar contra ella.' },
  {
    value: 'piloto',
    label: 'Piloto',
    nota: '§8 pide 30–40 entrevistas FUERA de las secciones seleccionadas, para medir duración y afinar el instrumento.',
  },
  {
    value: 'en_campo',
    label: 'En campo',
    nota: 'Levantamiento de la muestra real, dentro de las secciones del marco.',
  },
  {
    value: 'cerrada',
    label: 'Cerrada',
    nota: 'ATENCIÓN: sin ninguna oleada en piloto o en campo, la PWA no puede abrir entrevistas.',
  },
];

/**
 * Cambia la etapa de la oleada.
 *
 * El estado casi no cambia el comportamiento del sistema —sólo la resolución
 * de la oleada activa lo mira, y trata «piloto» y «en campo» por igual—, pero
 * sí cambia cómo se lee la base después: «piloto» sugiere datos de prueba, y
 * si el campo ya está abierto esa etiqueta es falsa.
 */
function WaveStage({
  wave,
}: {
  wave: { code: string; label: string; status: string; targetN: number };
}) {
  const { can } = useAdmin();
  const queryClient = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [destino, setDestino] = useState(wave.status);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cambiar = useMutation({
    mutationFn: () => api.setWaveStatus(wave.code, destino, motivo.trim()),
    onSuccess: () => {
      setAbierto(false);
      setMotivo('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['instrument'] });
      void queryClient.invalidateQueries({ queryKey: ['monitoring'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo cambiar la etapa.'),
  });

  const etapa = ETAPAS.find((e) => e.value === wave.status);

  return (
    <div className="panel">
      <div className="panel__head">
        <div>
          <h2 className="panel__title">
            Etapa de {wave.code} ·{' '}
            <StatusChip status={wave.status === 'en_campo' ? 'good' : 'warning'}>
              {etapa?.label ?? wave.status}
            </StatusChip>
          </h2>
          <p className="panel__note">{etapa?.nota}</p>
        </div>
        {can('instrument:write') && !abierto ? (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => {
              setAbierto(true);
              setDestino(wave.status);
            }}
          >
            Cambiar etapa
          </button>
        ) : null}
      </div>

      {abierto ? (
        <div className="stack">
          <label className="field" style={{ maxWidth: 320 }}>
            <span className="field__label">Nueva etapa</span>
            <select className="input" value={destino} onChange={(e) => setDestino(e.target.value)}>
              {ETAPAS.map((e) => (
                <option key={e.value} value={e.value}>
                  {e.label}
                </option>
              ))}
            </select>
            <span className="field__hint">
              {ETAPAS.find((e) => e.value === destino)?.nota}
            </span>
          </label>
          <label className="field" style={{ maxWidth: 420 }}>
            <span className="field__label">Motivo</span>
            <input
              className="input"
              value={motivo}
              placeholder="Cierre del piloto, apertura de campo…"
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={destino === wave.status || motivo.trim().length < 4 || cambiar.isPending}
              onClick={() => cambiar.mutate()}
            >
              {cambiar.isPending ? 'Guardando…' : 'Confirmar'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setAbierto(false)}>
              Cancelar
            </button>
          </div>
          {error ? (
            <div className="callout callout--danger" style={{ marginBottom: 0 }}>
              {error}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
